import { CategoryPropType, categoryProps } from './home.util';
import { ChangeDetectorRef, Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { Colors, DEFAULT_FAVORITE_COLOR } from '../../../utils/colors.util';
import { FavoriteRoute, FavoriteService } from 'src/services/favorite.service';
import { Observable, defer } from 'rxjs';
import { ReleaseMonth, ReleaseNote, getReleaseMonths } from './release-notes';
import { Router, RouterLink, UrlTree } from '@angular/router';
import { NotificationService } from 'src/services/notification.service';
import { Title } from '@angular/platform-browser';

// eslint-disable-next-line sort-imports
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    imports: [
        AccordionModule,
        ButtonModule,
        CardModule,
        CommonModule,
        ContextMenuModule,
        DialogModule,
        ProgressSpinnerModule,
        RouterLink,
        TieredMenuModule,
        TooltipModule,
    ],
})
export class HomeComponent implements OnInit {
    @ViewChild('favoriteContextMenu') public favoriteContextMenu!: ContextMenu;
    @ViewChild('favoriteMenu') public favoriteMenu!: TieredMenu;

    public categoryProps = [...categoryProps];
    public favoriteContextMenuItems: MenuItem[] = [
        {
            label: 'Choose background color',
            icon: 'pi pi-palette',
            styleClass: 'favorite-palette-menu-item',
            palette: true,
        },
        {
            separator: true,
        },
        {
            label: 'Remove from favorites',
            icon: 'pi pi-star',
            command: () => this.confirmRemoveFavorite(),
        },
    ];
    public favoriteMenuItems: MenuItem[] = [
        {
            label: 'Remove all favorites',
            icon: 'pi pi-trash',
            command: () => this.confirmRemoveAllFavorites(),
        },
    ];
    public favorites$!: Observable<FavoriteRoute[]>;
    public favoritesLoading$!: Observable<boolean>;
    public isReleaseNotesDialogVisible = false;
    public releaseMonths: ReleaseMonth[] = getReleaseMonths();
    public releaseNotesDialogNoteValue = '';
    public selectedFavorite: FavoriteRoute | null = null;
    public readonly defaultFavoriteColor = DEFAULT_FAVORITE_COLOR;
    public readonly favoriteColorVersion = signal(0);
    public readonly favoriteColors = Colors.slice(0, 5);
    public readonly releaseNotePreviewLineCount = 4;
    private changeDetectorRef = inject(ChangeDetectorRef);
    private confirmationService = inject(ConfirmationService);
    private favoriteService = inject(FavoriteService);
    private notificationService = inject(NotificationService);
    private router = inject(Router);
    private titleService = inject(Title);

    public constructor() {
        this.favorites$ = defer(() => this.favoriteService.favorites$);
        this.favoritesLoading$ = this.favoriteService.loading$;
    }

    public getFavoriteColor(favorite: FavoriteRoute): string {
        this.favoriteColorVersion();
        return favorite.color ?? this.defaultFavoriteColor;
    }

    public getFavoriteUrl(favorite: FavoriteRoute): UrlTree {
        return this.router.parseUrl(favorite.route);
    }

    public getHiddenReleaseNoteChangeLabel(note: ReleaseNote): string {
        const hiddenChangeCount = note.changes.length - this.releaseNotePreviewLineCount;
        return `${hiddenChangeCount} more ${hiddenChangeCount === 1 ? 'change' : 'changes'}`;
    }

    public getReleaseNoteDialogPanelValue(note: ReleaseNote, group: ReleaseMonth): string {
        return `${group.month}:${note.version}:${note.date}`;
    }

    public getReleaseNotePreview(note: ReleaseNote): string[] {
        return note.changes.slice(0, this.releaseNotePreviewLineCount);
    }

    public async handleCardClick(category: CategoryPropType) {
        await this.router.navigate([category.link]);
    }

    public hasHiddenReleaseNoteChanges(note: ReleaseNote): boolean {
        return note.changes.length > this.releaseNotePreviewLineCount;
    }

    public ngOnInit(): void {
        this.titleService.setTitle('Home - SesammTool 3');
    }

    public openAllReleaseNotesDialog(): void {
        const newestReleaseMonth = this.releaseMonths[0];
        const newestReleaseNote = newestReleaseMonth?.notes[0];

        if (!newestReleaseMonth || !newestReleaseNote) {
            return;
        }

        this.releaseNotesDialogNoteValue = this.getReleaseNoteDialogPanelValue(
            newestReleaseNote,
            newestReleaseMonth
        );
        this.isReleaseNotesDialogVisible = true;
    }

    public openReleaseNotesDialog(note: ReleaseNote, group: ReleaseMonth): void {
        this.releaseNotesDialogNoteValue = this.getReleaseNoteDialogPanelValue(note, group);
        this.isReleaseNotesDialogVisible = true;
    }

    public selectFavoriteColor(color: string): void {
        const favorite = this.selectedFavorite;
        if (!favorite) {
            return;
        }

        favorite.color = color;
        this.favoriteService.updateColor(favorite.route, color);
        this.favoriteColorVersion.update((version) => version + 1);
        this.favoriteContextMenu.hide();
        this.changeDetectorRef.detectChanges();
    }

    public selectFavoriteForContextMenu(favorite: FavoriteRoute): void {
        this.selectedFavorite = favorite;
    }

    private confirmRemoveAllFavorites(): void {
        this.confirmationService.confirm({
            message: 'Remove all pages from your favorites?',
            header: 'Remove All Favorites',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Remove all',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: async () => {
                try {
                    await this.favoriteService.removeAll();
                } catch (error) {
                    this.notificationService.handleError('Failed to remove all favorites', error);
                }
            },
        });
    }

    private confirmRemoveFavorite(): void {
        const favorite = this.selectedFavorite;
        if (!favorite) {
            return;
        }

        this.confirmationService.confirm({
            message: `Remove <b>${favorite.label}</b> from your favorites?`,
            header: 'Remove Favorite',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Remove',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: async () => {
                try {
                    await this.favoriteService.remove(favorite.route);
                } catch (error) {
                    this.notificationService.handleError('Failed to remove favorite', error);
                }
            },
        });
    }
}
